import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getCourses } from "../../redux/slice/courseSlice";
import { getInstitutes } from "../../redux/slice/instituteSlice";
import SearchIcon from '@mui/icons-material/Search';
import { Button, TextField, Grid, Typography, IconButton, Select, MenuItem, FormControl, InputLabel, Card, CardMedia, CardContent, InputAdornment } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import EventIcon from '@mui/icons-material/Event';

const AllCourses = ({ limit }) => {
  const [sort, setSort] = useState(""); // State for sorting
  const [search, setSearch] = useState("");
  const { courses, totalPage } = useSelector((state) => state.course);
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  const [locationSearch, setLocationSearch] = useState("");
  const [isListMode, setIsListMode] = useState(false); // State for grid/list view

  useEffect(() => {
    dispatch(getCourses({ page, sort, search, location: locationSearch, limit: 10 }));
  }, [dispatch, page, sort, search, locationSearch, limit]);

  const searchHandle = (e) => {
    e.preventDefault();
    setPage(1);
    dispatch(getCourses({ page: 1, sort, search, location: locationSearch, limit: 10 }));
  };

  // Function to handle sorting and filtering courses
  const sortedCourses = () => {
    if (!courses) return [];
    let filteredCourses = [...courses];

    // Filter by location
    if (locationSearch) {
      filteredCourses = filteredCourses.filter(course =>
        course.locationName.toLowerCase().includes(locationSearch.toLowerCase())
      );
    }

    // Sort based on selected sort option
    switch (sort) {
      case "priceAsc":
        // Sort by price (low to high)
        filteredCourses.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case "priceDesc":
        // Sort by price (high to low)
        filteredCourses.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case "newest":
        // Sort by newest first
        filteredCourses.sort((a, b) => new Date(b.courseStartDate) - new Date(a.courseStartDate));
        break;
      default:
        // No sorting (default order)
        break;
    }

    return filteredCourses.slice(0, limit); // Return the sorted and limited courses
  };

  const handleSortChange = (event) => {
    const sortOption = event.target.value;
    setSort(sortOption);
    setPage(1); // Reset page when sort option is changed
    dispatch(getCourses({ page: 1, sort: sortOption, search, location: locationSearch, limit }));
  };

  return (
    <>
      <div>
        <Typography variant="h1" component="h5" sx={{ color: '#2596be', fontWeight: 'bold', fontSize: '30px' }}>
          Drone Pilot <span style={{ color: '#000000' }}>Training Courses</span>
        </Typography>

        {/* Search and Filter Section */}
        <Grid container spacing={2} sx={{ marginTop: '5px' }} alignItems="center">
          {/* Search Drone Courses */}
          <Grid item xs={12} sm={4} md={4}>
            <form className="d-flex" onSubmit={searchHandle}>
              <TextField
                variant="outlined"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Drone Courses"
                sx={{
                  width: { xs: '100%', sm: '280px' }, // Full width on mobile, fixed width on larger screens
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '36px',
                    fontSize: '14px',
                    paddingRight: '10px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 10px',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        type="submit"
                        disableRipple
                        disableElevation
                        sx={{
                          minWidth: 'auto',
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                          '&:active': {
                            backgroundColor: 'transparent',
                          },
                          cursor: 'default', // optional: if you don't want pointer cursor
                        }}
                      >
                        <SearchIcon sx={{ fontSize: '20px', color: '#2596be' }} />
                      </Button>

                    </InputAdornment>
                  ),
                }}
              />
            </form>
          </Grid>

          {/* Search by Location */}
          <Grid item xs={12} sm={3} md={3}>
            <form
              className="d-flex"
              onSubmit={(e) => {
                e.preventDefault();
                setPage(1);
                dispatch(getCourses({ page: 1, sort, search, location: locationSearch, limit }));
              }}
            >
              <TextField
                variant="outlined"
                fullWidth
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                placeholder="Search by Location"
                sx={{
                  width: { xs: '100%', sm: '290px' }, // Full width on mobile, fixed width on larger screens
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '36px',
                    fontSize: '13px',
                    paddingRight: '10px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 10px',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button type="submit"
                        disableRipple
                        disableElevation
                        sx={{
                          minWidth: 'auto',
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                          '&:active': {
                            backgroundColor: 'transparent',
                          },
                          cursor: 'default', // optional: if you don't want pointer cursor
                        }}>
                        <SearchIcon sx={{ fontSize: '20px', color: '#2596be' }} />
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </form>
          </Grid>

          {/* Grid/List View Toggle Icons */}
          <Grid item xs={12} sm={2} md={2} sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center' }}>
            <IconButton onClick={() => setIsListMode(true)} sx={{ marginRight: '8px' }}>
              <ViewListIcon sx={{ fontSize: 25, color: isListMode ? '#2596be' : '#777' }} />
            </IconButton>
            <IconButton onClick={() => setIsListMode(false)}>
              <GridViewIcon sx={{ fontSize: 25, color: !isListMode ? '#2596be' : '#777' }} />
            </IconButton>
          </Grid>

          {/* Sort By Dropdown */}
          <Grid item xs={12} sm={3} md={3}>
            <FormControl fullWidth sx={{ height: '36px' }}>
              <InputLabel sx={{ fontSize: '14px', top: '-5px' }}>Sort By</InputLabel>
              <Select
                value={sort}
                onChange={handleSortChange}
                label="Sort By"
                sx={{
                  borderRadius: '8px',
                  height: '36px',
                  fontSize: '14px',
                  '& .MuiSelect-select': {
                    padding: '8px 10px',
                  },
                }}
              >
                <MenuItem value="priceAsc">Price -- Low to High</MenuItem>
                <MenuItem value="priceDesc">Price -- High to Low</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Course List */}
        <Grid container spacing={4} sx={{ marginTop: '32px' }}>
  {sortedCourses().map((obj, index) => (
    <Grid item xs={12} sm={isListMode ? 12 : 6} md={isListMode ? 12 : 4} key={index}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '15px',
        }}
      >
        <Link
          to={`/courses-details/${obj._id}`}
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <CardMedia
            component="img"
            height={isListMode ? '300' : '200'}
            image={obj.image}
            alt="course-image"
          />

          {/* CardContent takes full remaining space and aligns bottom content */}
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flexGrow: 1,
              backgroundColor: '#fff',
              borderBottomLeftRadius: '15px',
              borderBottomRightRadius: '15px',
            }}
          >
            {/* Top content (title, location, date) */}
            <div>
              <Typography
                variant="h6"
                sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}
              >
                {obj.courseName}
              </Typography>

              {/* Course By Section */}
              <Typography variant="body2" sx={{ color: '#007bff', fontWeight: 500, mb: 1 }}>
                Course By: <span style={{ color: '#007bff' }}>{getInstitutes?.instituteName}</span>
              </Typography>

              <div className="d-flex gap-2 align-items-center pt-2">
                <LocationOnIcon sx={{ fontSize: '15px' }} />
                <Typography variant="body2" sx={{ color: '#777', fontSize: '15px' }}>
                  {obj.locationName}
                </Typography>
              </div>

              <div className="d-flex gap-2 align-items-center pt-1">
                <EventIcon sx={{ fontSize: '15px' }} />
                <Typography variant="body2" sx={{ color: '#777', fontSize: '15px' }}>
                  {obj.courseStartDate}
                </Typography>
              </div>
            </div>

            {/* Bottom content (price and button) */}
            <div
              className="d-flex justify-content-between align-items-center pt-4"
              style={{ marginTop: 'auto' }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 'bold', color: '#2596be', fontSize: '20px' }}
              >
                ₹ {new Intl.NumberFormat('en-IN').format(obj.price)}
              </Typography>

              <Button
                variant="outlined"
                sx={{
                  backgroundColor: 'white',
                  color: '#1976D2',
                  border: '2px solid #1976D2',
                  padding: '6px 12px',
                  fontWeight: '500',
                  fontSize: '12px',
                  textTransform: 'none',
                  transition: '0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#1976D2',
                    color: 'white',
                  },
                }}
              >
                Enroll Now
              </Button>
            </div>
          </CardContent>
        </Link>
      </Card>
    </Grid>
  ))}
</Grid>


        {/* Load More Button */}
        {totalPage > 1 && (
  <Grid container justifyContent="center" sx={{ mt: 5 }}>
    <Stack spacing={2}>
      <Pagination
        count={totalPage}
        page={page}
        color="primary"
        shape="rounded"
        onChange={(event, value) => {
          setPage(value);
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }}
      />
    </Stack>
  </Grid>
)}
      </div>
    </>
  );
};

export default AllCourses;